import { message } from "antd";
import { jwtDecode } from "jwt-decode";
import posthog from "posthog-js";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../api";
import { OverflowFrame, PageHeading } from "../externalComponents";
import { BannerNewLibraryFlow } from "../externalComponents/BannerNewLibraryFlow";
import { AccessOngoingDrafts } from "../externalComponents/dashboard/AccessOngoinDrafts";
import { DashboardRevenue } from "../externalComponents/dashboard/DashboardRevenue";
import { DialogJoinCompany } from "../externalComponents/dashboard/DialogJoinCompany";
import { DisplayAdvices } from "../externalComponents/dashboard/DisplayAdvices";
import { ViewActiveAutomations } from "../externalComponents/dashboard/ViewActiveAutomations";
import { ViewPastCampaigns } from "../externalComponents/dashboard/ViewPastCampaigns";
import { ViewScheduledCampaigns } from "../externalComponents/dashboard/ViewScheduledCampaigns";
import { ViewStoppedCampaigns } from "../externalComponents/dashboard/ViewStoppedCampaigns";
import { cleanInvitationUrl } from "../externalComponents/invitation-page";
import { Button } from "../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../externalComponents/tailwind/dialog";
import { Heading } from "../externalComponents/tailwind/heading";
import { Text } from "../externalComponents/tailwind/text";
import { useIsMobile } from "../externalComponents/useIsMobile";
import { WhatsappQualityScoreBanner } from "../externalComponents/WhatsappQualityScoreBanner";
import { getPromotedLibraryFlows } from "../functions/getPromotedLibraryFlows";
import { CustomerContext } from "../providers";

export default function HomePage() {
  const navigate = useNavigate();

  const { user, customer, shop, integrations } = useContext(CustomerContext);
  const [dataLibraryFlowCampaigns, setDataLibraryFlowCampaigns] = useState([]);
  const [dataLibraryFlowAutomations, setDataLibraryFlowAutomations] = useState(
    [],
  );

  useEffect(() => {
    const loadPromotedFlows = async () => {
      if (!customer?.id) {
        return; // Ne pas faire d'appels API si customer n'existe pas
      }
      try {
        const campaigns = await getPromotedLibraryFlows({
          isAutomation: false,
        });
        const automations = await getPromotedLibraryFlows({
          isAutomation: true,
        });

        const existingCampaign = await api.campaign.maybeFindFirst({
          filter: {
            isArchived: { notEquals: true },
            customerId: { equals: customer.id },
            trigger: { equals: "manual" },
          },
          select: {
            id: true,
          },
        });

        if (existingCampaign) {
          setDataLibraryFlowCampaigns(campaigns);
          setDataLibraryFlowAutomations(automations);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des flows promus:", error);
      }
    };

    loadPromotedFlows();
  }, []);

  useEffect(() => {
    if (
      !customer?.apiKey360Dialog &&
      !customer?.waAccessToken &&
      !customer?.alcmeonCompanyId &&
      customer?.isSubscribed
    ) {
      navigate("/settings/whatsapp/connexion");
    }
  }, [customer]);

  const isMobile = useIsMobile();

  return (
    <>
      <PageHeading
        title={`Hello ${user.firstName} 🎉`}
        {...(!isMobile &&
          customer.isSubscribed && {
            primaryAction: "New campaign",
            handlePrimaryAction: async () => {
              posthog.capture("user_clicked_creation_shortcut", {
                scenario_type: "campaign",
              });
              navigate("/campaigns/new");
            },
            secondaryAction: "New automation",
            handleSecondaryAction: async () => {
              posthog.capture("user_clicked_creation_shortcut", {
                scenario_type: "automation",
              });
              navigate("/automations/new");
            },
            thirdAction: "New chatbot",
            handleThirdAction: async () => {
              posthog.capture("user_clicked_creation_shortcut", {
                scenario_type: "chatbot",
              });
              navigate("/chatbots/new");
            },
          })}
      />
      <OverflowFrame>
        <PageMarkup
          shop={shop}
          customer={customer}
          isMobile={isMobile}
          integrations={integrations}
          user={user}
          dataLibraryFlowCampaigns={dataLibraryFlowCampaigns}
          dataLibraryFlowAutomations={dataLibraryFlowAutomations}
        />
      </OverflowFrame>
    </>
  );
}

function PageMarkup({
  shop,
  customer,
  isMobile,
  integrations,
  user,
  dataLibraryFlowCampaigns,
  dataLibraryFlowAutomations,
}) {
  const [isLinkingModalOpened, setIsLinkingModalOpened] = useState(false);
  const [isJoinCompanyModalOpened, setIsJoinCompanyModalOpened] =
    useState(false);

  const cleanUrl = () => {
    if (shop?.id) {
      const url = new URL(window.location.href);
      url.search = ""; // Remove the query string
      window.history.replaceState({}, document.title, url.pathname);
    }
  };

  const handleLinkingShopifyToCustomer = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!shop?.id && urlParams.get("origin")) {
      console.log("Want to link the shopify account?");
      const decodedToken = jwtDecode(urlParams.get("origin"));
      if (decodedToken.shopId && customer.id) {
        try {
          await api.customer.update(customer.id, {
            shop: { _link: decodedToken.shopId.toString() },
            shopifyShopId: decodedToken.shopId.toString(),
          });
          message.success("Shopify is now connected.");
        } catch (error) {
          console.error(error);
          message.error("Shopify connexion has failed.");
        }
      }
    }

    cleanUrl();
    setIsLinkingModalOpened(false);
  };

  useEffect(() => {
    const shouldLinkShopifyToCustomer = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      // Show modal to link the customer and the shopifyShop
      if (!shop?.id && urlParams.get("origin")) {
        const customerRecord = await api.customer.maybeFindOne(customer.id, {
          select: { shopId: true },
        });
        if (!customerRecord.shopId) {
          setIsLinkingModalOpened(true);
        } else {
          cleanUrl();
        }
      }
    };

    const doesUserJustResetPassword = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("reset_password_token")) {
        const url = new URL(window.location.href);
        url.search = ""; // Remove the query string
        window.history.replaceState({}, document.title, url.pathname);
      }
    };

    shouldLinkShopifyToCustomer();
    doesUserJustResetPassword();
    const urlParams = new URLSearchParams(window.location.search);
    const invitationCode = urlParams.get("invitation_code");
    setIsJoinCompanyModalOpened(!!invitationCode);
  }, []);

  return (
    <>
      <div className="m-3 md:m-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-3 mt-3">
          {!isMobile && (
            <AccessOngoingDrafts
              className="mt-3 h-full tab:h-[12rem]"
              customerId={customer.id}
            />
          )}

          <WhatsappQualityScoreBanner
            className="my-3 h-full tab:h-[12rem]"
            customer={customer}
            isMobile={isMobile}
          />
        </div>

        <div className="mt-2">
          <DashboardRevenue
            customerId={user.customerId}
            shopId={shop?.id}
            isMobile={isMobile}
            integrations={integrations}
          />
        </div>
      </div>

      <div className="mt-6">
        <DisplayAdvices customer={customer} user={user} />
      </div>
      <div className="mt-6">
        <ViewScheduledCampaigns
          customerId={customer.id}
          isMobile={isMobile}
          integrations={integrations}
        />
      </div>
      <div className="mt-6">
        <ViewStoppedCampaigns customerId={customer.id} isMobile={isMobile} />
      </div>
      <div className="mt-6">
        <BannerNewLibraryFlow
          dataLibraryFlow={dataLibraryFlowCampaigns}
          hasAutomations={false}
          title="Recommended campaigns"
          user={user}
        />
        <ViewPastCampaigns
          customerId={customer.id}
          shopId={shop?.id}
          isMobile={isMobile}
          integrations={integrations}
        />
      </div>

      <div className="mt-6">
        <BannerNewLibraryFlow
          dataLibraryFlow={dataLibraryFlowAutomations}
          hasAutomations={true}
          title="Recommended automations"
          user={user}
        />
        <ViewActiveAutomations
          customerId={customer.id}
          shopId={shop?.id}
          isMobile={isMobile}
          integrations={integrations}
        />
      </div>

      <Dialog
        key="dialog_preview"
        open={isLinkingModalOpened}
        className="max-h-[80vh] flex flex-col"
        onClose={async () => {
          setIsLinkingModalOpened(false);
        }}
      >
        <DialogTitle>Connect Shopify</DialogTitle>
        <DialogBody className="flex-1 overflow-y-auto scrollbar">
          <Heading>{customer?.name}</Heading>
          <Text>
            By clicking on Connect, you'll sync your Shopify data to this{" "}
            {customer?.name}'s Simio account
          </Text>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={async () => {
              setIsLinkingModalOpened(false);
            }}
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={handleLinkingShopifyToCustomer}
          >
            Connect
          </Button>
        </DialogActions>
      </Dialog>

      <DialogJoinCompany
        isOpen={isJoinCompanyModalOpened}
        onClose={() => {
          setIsJoinCompanyModalOpened(false);
          cleanInvitationUrl();
        }}
        cleanInvitationUrl={cleanInvitationUrl}
      />
    </>
  );
}
